import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { toast } from "sonner";
import { fmtINR } from "../lib/format";
import { RefreshCw, Loader2 } from "lucide-react";
import {
    ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const PERIOD_OPTS = [
    { key: "fy", label: "This FY" },
    { key: "prev_fy", label: "Previous FY" },
    { key: "3m", label: "Last 3M" },
    { key: "6m", label: "Last 6M" },
    { key: "12m", label: "Last 12M" },
    { key: "all", label: "All Data" },
];

const monthShort = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    return `${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][parseInt(m, 10) - 1]}-${y.slice(-2)}`;
};

const pctColor = (v) => (v >= 20 ? "text-[var(--pos)]" : v >= 10 ? "text-[var(--brand-amber)]" : "text-[var(--neg)]");
const signColor = (v) => (v > 0 ? "text-[var(--pos)]" : v < 0 ? "text-[var(--neg)]" : "text-[var(--text-secondary)]");

export default function PnL() {
    const [period, setPeriod] = useState("fy");
    const [accountId, setAccountId] = useState("");
    const [accounts, setAccounts] = useState([]);
    const [pnl, setPnl] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const url = accountId ? `/pnl?period=${period}&account_id=${accountId}` : `/pnl?period=${period}`;
            const r = await api.get(url);
            setPnl(r.data);
        } catch (e) {
            toast.error("Failed to load P&L");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        api.get("/bank-import/accounts-summary").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [period, accountId]);

    const w = pnl?.waterfall || {};
    const buckets = pnl?.buckets || [];
    const monthly = pnl?.monthly_series || [];

    const chartData = useMemo(() => {
        return monthly.map((m) => ({
            month: monthShort(m.month),
            Revenue: Math.round(m.revenue || 0),
            "Direct Costs": -Math.round(m.direct_costs || 0),
            "Emp Cost": -Math.round(m.employee_costs || 0),
            Overhead: -Math.round(m.overhead || 0),
            Statutory: -Math.round(m.statutory || 0),
            Finance: -Math.round(m.finance || 0),
            PBT: Math.round(m.pbt || 0),
            EBITDA: Math.round(m.ebitda || 0),
        }));
    }, [monthly]);

    const waterfallData = useMemo(() => {
        if (!w) return [];
        return [
            { name: "Revenue", value: w.revenue, kind: "pos" },
            { name: "Direct Costs", value: -Math.abs(w.direct_costs || 0), kind: "neg" },
            { name: "Gross Profit", value: w.gross_profit, kind: "sub", pct: w.gross_margin_pct },
            { name: "Employee", value: -Math.abs(w.employee_costs || 0), kind: "neg" },
            { name: "Overhead", value: -Math.abs(w.overhead || 0), kind: "neg" },
            { name: "EBITDA", value: w.ebitda, kind: "sub", pct: w.ebitda_margin_pct },
            { name: "Statutory", value: -Math.abs(w.statutory || 0), kind: "neg" },
            { name: "Finance", value: -Math.abs(w.finance || 0), kind: "neg" },
            { name: "PBT", value: w.pbt, kind: "sub", pct: w.net_margin_pct },
        ];
    }, [w]);

    return (
        <div className="space-y-4" data-testid="pnl-page">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// LIVE P&L · AUTO-BUILT FROM BANK CATEGORIES</div>
                    <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Profit & Loss</h1>
                    <div className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                        Auto-computed from categorised bank transactions · No manual entry
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        data-testid="select-pnl-period"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="bg-[var(--surface)] border border-[var(--border)] px-3 py-1.5 text-xs font-mono-fin uppercase"
                    >
                        {PERIOD_OPTS.map((p) => (
                            <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                    </select>
                    <select
                        data-testid="select-pnl-account"
                        value={accountId}
                        onChange={(e) => setAccountId(e.target.value)}
                        className="bg-[var(--surface)] border border-[var(--border)] px-3 py-1.5 text-xs font-mono-fin uppercase"
                    >
                        <option value="">All Accounts</option>
                        {accounts.map((a) => (
                            <option key={a.id} value={a.id}>{a.bank_name} · {a.id}</option>
                        ))}
                    </select>
                    <button
                        data-testid="btn-pnl-refresh"
                        onClick={load}
                        className="flex items-center gap-1 px-3 py-1.5 border border-[var(--brand-amber)] text-[var(--brand-amber)] font-mono-fin text-[10px] uppercase font-bold hover:bg-[var(--brand-amber)]/10"
                    >
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
                    </button>
                </div>
            </div>

            {loading && !pnl && (
                <div className="p-12 border border-dashed border-[var(--border)] text-center">
                    <Loader2 size={32} className="mx-auto animate-spin text-[var(--brand-amber)]" />
                    <div className="mt-2 font-mono-fin text-xs text-[var(--text-secondary)]">Aggregating bank transactions…</div>
                </div>
            )}

            {pnl && (
                <>
                    {/* KPI STRIP */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard testId="kpi-revenue" label="REVENUE" value={w.revenue} sub={`${pnl.meta?.categorised_pct || 0}% categorised`} accent />
                        <KpiCard testId="kpi-gp" label="GROSS PROFIT" value={w.gross_profit} sub={`${w.gross_margin_pct}% margin`} />
                        <KpiCard testId="kpi-ebitda" label="EBITDA" value={w.ebitda} sub={`${w.ebitda_margin_pct}% margin`} />
                        <KpiCard testId="kpi-pbt" label="PBT" value={w.pbt} sub={`${w.net_margin_pct}% net margin`} />
                    </div>

                    {/* WATERFALL */}
                    <Panel
                        title="Waterfall · Revenue to PBT"
                        subtitle={`Period ${pnl.period?.label} · ${pnl.period?.start || "—"} → ${pnl.period?.end || "—"} · ${pnl.meta?.total_txns || 0} txns`}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-9 gap-2">
                            {waterfallData.map((step, i) => (
                                <div
                                    key={step.name}
                                    data-testid={`wf-${step.name.replace(/\s+/g, "-").toLowerCase()}`}
                                    className={`p-3 border border-[var(--border)] bg-[var(--surface)]/40 ${
                                        step.kind === "sub" ? "border-[var(--brand-amber)]/60 bg-[var(--brand-amber)]/5" : ""
                                    }`}
                                >
                                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                        {step.name}
                                    </div>
                                    <div className={`text-lg font-bold mt-1 font-mono-fin ${signColor(step.value)}`}>
                                        {step.value >= 0 ? "+" : ""}{fmtINR(step.value)}
                                    </div>
                                    {step.pct !== undefined && (
                                        <div className={`text-[10px] font-mono-fin mt-0.5 ${pctColor(step.pct)}`}>
                                            {step.pct}% margin
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Panel>

                    {/* MONTHLY CHART */}
                    {chartData.length > 0 && (
                        <Panel title="Monthly Trend" subtitle="Revenue vs Costs · EBITDA line overlay">
                            <div style={{ width: "100%", height: 320 }}>
                                <ResponsiveContainer>
                                    <ComposedChart data={chartData} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
                                        <CartesianGrid stroke="#E2E8F0" vertical={false} />
                                        <XAxis dataKey="month" stroke="#475569" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }} />
                                        <YAxis stroke="#475569" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }}
                                            tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
                                        <Tooltip
                                            contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 11, fontFamily: "IBM Plex Mono, monospace" }}
                                            formatter={(v) => fmtINR(v)}
                                        />
                                        <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }} />
                                        <Bar dataKey="Revenue" stackId="a" fill="#4af6c3" />
                                        <Bar dataKey="Direct Costs" stackId="b" fill="#e94b6a" />
                                        <Bar dataKey="Emp Cost" stackId="b" fill="#f6a1a6" />
                                        <Bar dataKey="Overhead" stackId="b" fill="#c084fc" />
                                        <Bar dataKey="Statutory" stackId="b" fill="#60a5fa" />
                                        <Bar dataKey="Finance" stackId="b" fill="#f97316" />
                                        <Line type="monotone" dataKey="EBITDA" stroke="#fb8b1e" strokeWidth={2} dot={{ r: 3 }} />
                                        <Line type="monotone" dataKey="PBT" stroke="#fff" strokeWidth={2} strokeDasharray="3 3" dot={{ r: 2 }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </Panel>
                    )}

                    {/* CATEGORY DRILL-DOWN */}
                    <Panel title="Category Drill-Down" subtitle="Individual categories rolled into P&L buckets">
                        <div className="overflow-auto max-h-[60vh]">
                            <table className="w-full text-sm">
                                <thead className="border-b border-[var(--border)] font-mono-fin text-[10px] uppercase text-[var(--text-secondary)] sticky top-0 bg-[var(--panel)] z-10">
                                    <tr>
                                        <th className="text-left py-2 px-2">Bucket</th>
                                        <th className="text-left px-2">Category</th>
                                        <th className="text-right px-2">Txns</th>
                                        <th className="text-right px-2">Debit</th>
                                        <th className="text-right px-2">Credit</th>
                                        <th className="text-right px-2">Net Impact</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {buckets.map((b) => (
                                        <React.Fragment key={b.key}>
                                            <tr className="bg-[var(--brand-amber)]/5 border-t-2 border-[var(--brand-amber)]/30" data-testid={`bucket-row-${b.key}`}>
                                                <td className="py-2 px-2 font-mono-fin text-[11px] uppercase text-[var(--brand-amber)] font-bold" colSpan={2}>
                                                    {b.label}
                                                </td>
                                                <td className="text-right px-2 font-mono-fin text-xs">{b.count}</td>
                                                <td className="text-right px-2"></td>
                                                <td className="text-right px-2"></td>
                                                <td className={`text-right px-2 font-mono-fin text-sm font-bold ${signColor(b.amount)}`}>
                                                    {b.amount >= 0 ? "+" : ""}{fmtINR(b.amount)}
                                                </td>
                                            </tr>
                                            {b.details.map((d) => (
                                                <tr key={`${b.key}-${d.category}`} className="border-b border-[var(--border)] hover:bg-[var(--surface)]/40">
                                                    <td className="py-1.5 px-2 text-[10px] text-[var(--text-muted)]"></td>
                                                    <td className="px-2 text-xs">{d.category}</td>
                                                    <td className="px-2 text-right text-xs font-mono-fin">{d.count}</td>
                                                    <td className="px-2 text-right text-xs font-mono-fin text-[var(--neg)]">
                                                        {d.debit > 0 ? fmtINR(d.debit) : ""}
                                                    </td>
                                                    <td className="px-2 text-right text-xs font-mono-fin text-[var(--pos)]">
                                                        {d.credit > 0 ? fmtINR(d.credit) : ""}
                                                    </td>
                                                    <td className={`px-2 text-right text-xs font-mono-fin ${signColor(d.amount)}`}>
                                                        {fmtINR(d.amount)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <div className="text-[10px] font-mono-fin text-[var(--text-muted)] text-center py-2">
                        Auto-built from {pnl.meta?.total_txns || 0} bank transactions ·{" "}
                        {pnl.meta?.categorised_pct || 0}% categorised · Uncategorised rows are excluded from the waterfall
                    </div>
                </>
            )}
        </div>
    );
}
